import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, CircularProgress, Alert, Box, Button, IconButton, Link } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useBreadcrumb } from '../components/BreadcrumbContext';
import { apiFetch } from '../apiFetch';
import { Grocery } from '../types/Grocery';
import GroceryFormModal from '../modals/GroceryFormModal';
import DeleteModal from '../modals/DeleteModal';

interface LocationState {
  fromShoppingList?: {
    id: number;
    name: string;
  };
}

const ViewGrocery: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [grocery, setGrocery] = useState<Grocery | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setOverrideLabel } = useBreadcrumb();

  // Get shopping list origin from location state
  const locationState = location.state as LocationState | null;
  const fromShoppingList = locationState?.fromShoppingList;

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Usage check state
  const [usageLoading, setUsageLoading] = useState(false);
  const [groceryUsage, setGroceryUsage] = useState<{
    usedInLists: { id: number; name: string }[];
    hasMore: boolean;
  }>({ usedInLists: [], hasMore: false });

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchGrocery = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/groceries/${id}/`);
        setGrocery(data);
        setOverrideLabel(data.name);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching grocery details.');
      } finally {
        setLoading(false);
      }
    };
    fetchGrocery();
  }, [apiUrl, id, setOverrideLabel]);

  const handleEditSuccess = (updatedGrocery: Grocery) => {
    setGrocery(updatedGrocery);
    setOverrideLabel(updatedGrocery.name);
  };

  // Handle delete button click with usage check
  const handleDeleteButtonClick = async () => {
    if (!grocery) return;

    setUsageLoading(true);
    setDeleteModalOpen(true);

    try {
      const usage = await apiFetch(`${apiUrl}/api/groceries/${grocery.id}/usage/`);
      setGroceryUsage({
        usedInLists: usage.open_lists || [],
        hasMore: usage.has_more || false,
      });
    } catch (err) {
      setGroceryUsage({ usedInLists: [], hasMore: false });
    } finally {
      setUsageLoading(false);
    }
  };

  // Handle closing the Delete modal
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setGroceryUsage({ usedInLists: [], hasMore: false });
  };

  const handleDeleteConfirm = async () => {
    if (!grocery) return;
    try {
      await apiFetch(`${apiUrl}/api/groceries/${grocery.id}/`, {
        method: 'DELETE',
      });
      navigate('/groceries', { state: { message: 'Grocery deleted successfully.' } });
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the grocery.');
      handleDeleteModalClose();
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />;
  }
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!grocery) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="warning">Grocery not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/groceries')} aria-label="back to groceries">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">{grocery.name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {fromShoppingList && (
            <Button
              component={RouterLink}
              to={`/shopping-lists/${fromShoppingList.id}`}
              endIcon={<ArrowForwardIcon />}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {fromShoppingList.name}
            </Button>
          )}
          <Box className="page-header-buttons">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setFormModalOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteButtonClick}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        component="img"
        src={grocery.image || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
        alt={grocery.name}
        sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', mb: 2 }}
      />

      {grocery.brand && (
        <Typography variant="body1" gutterBottom>
          <strong>Brand:</strong> {grocery.brand}
        </Typography>
      )}
      <Typography variant="body1" gutterBottom>
        <strong>Category:</strong> {grocery.category}
      </Typography>
      {grocery.unit && (
        <Typography variant="body1" gutterBottom>
          <strong>Unit:</strong> {grocery.unit}
        </Typography>
      )}
      {grocery.packaging_size && (
        <Typography variant="body1" gutterBottom>
          <strong>Packaging Size:</strong> {grocery.packaging_size}
        </Typography>
      )}
      {grocery.barcode && (
        <Typography variant="body1" gutterBottom>
          <strong>Barcode:</strong> {grocery.barcode}
        </Typography>
      )}
      {grocery.description && (
        <Typography variant="body1" gutterBottom>
          <strong>Description:</strong> {grocery.description}
        </Typography>
      )}
      {grocery.notes && (
        <Typography variant="body1" gutterBottom>
          <strong>Notes:</strong> {grocery.notes}
        </Typography>
      )}

      {/* Open Food Facts attribution for catalog items */}
      {grocery.catalog_item_id && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          Product data from{' '}
          <Link
            href="https://world.openfoodfacts.org"
            target="_blank"
            rel="noopener noreferrer"
            color="text.secondary"
            underline="hover"
          >
            Open Food Facts
          </Link>
          {' '}(ODbL)
        </Typography>
      )}

      {/* Edit Grocery Modal */}
      <GroceryFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleEditSuccess}
        grocery={grocery}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteConfirm}
        itemName={grocery.name}
        loading={usageLoading}
        usedInLists={groceryUsage.usedInLists}
        hasMoreLists={groceryUsage.hasMore}
      />
    </Container>
  );
};

export default ViewGrocery;